import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { useQuery } from "@tanstack/react-query";
import Sidebar from "@/components/layout/Sidebar";
import FloatingCta from "@/components/layout/FloatingCta";
import Footer from "@/components/layout/Footer";
import { api } from "@/lib/api";
import { useLang } from "@/hooks/useLang";
import { baseGraph } from "@/lib/schema";

export default function Layout() {
  const location = useLocation();
  const lenisRef = useRef(null);
  const { lang } = useLang();

  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: api.getProfile });

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname]);

  // Inject sitewide base schema (WebSite + Organization + Person) on every page
  useEffect(() => {
    if (!profile) return;
    document.querySelectorAll('script[data-base-jsonld="true"]').forEach((n) => n.remove());
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-base-jsonld", "true");
    s.textContent = JSON.stringify(baseGraph(profile, lang));
    document.head.appendChild(s);
  }, [profile, lang]);

  return (
    <div className="relative min-h-screen bg-background">
      <div className="grain-overlay" />
      <Sidebar profile={profile} />
      <div className="lg:pl-[280px]">
        <FloatingCta profile={profile} />
        <main className="min-h-[70vh]">
          <Outlet context={{ profile }} />
        </main>
        <Footer profile={profile} />
      </div>
    </div>
  );
}
